import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, MessageSquareWarning, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { SettlementWorking } from '../components/documents/SettlementWorking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/offboarding/public/leave-settlement`;
const money = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg bg-gray-50 p-3">
    <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="font-semibold text-gray-900" data-testid={testid}>{value}</p>
  </div>
);

const SignoffStatus = ({ s }) => (
  <div className={`rounded-lg border p-3 text-sm ${s.status === 'accepted' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`} data-testid="signoff-status">
    {s.status === 'accepted'
      ? <p className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> You accepted this statement on {fmtDate(s.responded_at)}.</p>
      : <><p className="flex items-center gap-2"><MessageSquareWarning className="h-4 w-4" /> You raised a query on {fmtDate(s.responded_at)}. HR will contact you.</p>
        <p className="mt-1 text-xs italic">"{s.reason}"</p></>}
  </div>
);

export default function SettlementConfirm() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${token}/info`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || 'This link is invalid or has expired.'));
  }, [token]);

  const respond = async (action) => {
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/${token}/respond`, { action, reason });
      setD((p) => ({ ...p, signoff: r.data })); setMode(null);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not submit. Please try again.'); }
    finally { setBusy(false); }
  };

  if (!d) return (
    <div className="min-h-screen flex items-center justify-center bg-violet-50 p-6 text-sm text-gray-600" data-testid="settlement-confirm-loading">
      {err || <Loader2 className="h-5 w-5 animate-spin text-violet-600" />}
    </div>
  );
  const s = d.signoff;
  return (
    <div className="min-h-screen bg-violet-50 py-8 px-4" data-testid="settlement-confirm-page">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-sm space-y-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-violet-600">Final Leave Salary Statement</p>
          <h1 className="text-2xl font-bold text-gray-900">{d.employee_name} <span className="text-sm font-normal text-gray-500">{d.employee_code}</span></h1>
          <p className="text-sm text-gray-500">{d.position} · {d.department} · Joined {d.join_date} · Last working day {d.last_working_day}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Service" value={`${d.service_years}y ${d.service_months}m ${d.service_days}d`} />
          <Stat label="Leave taken" value={d.already_taken_days} />
          <Stat label="Pending balance" value={`${d.balance_days} days`} testid="confirm-balance" />
          <Stat label="Daily rate" value={`${d.currency} ${money(d.daily_rate)}`} />
        </div>
        <SettlementWorking data={d} />
        <div className="rounded-xl bg-violet-600 p-4 text-white flex justify-between items-center">
          <div><p className="text-xs uppercase opacity-80">Amount payable</p><p className="text-xs opacity-70">{d.balance_days} days × {d.currency} {money(d.daily_rate)}</p></div>
          <p className="text-2xl font-bold" data-testid="confirm-amount">{d.currency} {money(d.net_settlement ?? d.leave_salary)}</p>
        </div>
        <a href={`${API}/${token}?format=pdf`} className="inline-flex items-center gap-1 text-sm text-violet-700 hover:underline" data-testid="confirm-download-pdf"><Download className="h-4 w-4" /> Download PDF</a>
        {s && <SignoffStatus s={s} />}
        {err && <p className="text-sm text-rose-600" data-testid="confirm-error">{err}</p>}
        {s?.status !== 'accepted' && (
          mode === 'query' ? (
            <div className="space-y-2">
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain what you think is incorrect…" data-testid="confirm-query-reason" />
              <div className="flex gap-2">
                <Button onClick={() => respond('query')} disabled={busy || reason.trim().length < 5} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-query-submit">Submit query</Button>
                <Button variant="outline" onClick={() => setMode(null)} data-testid="confirm-query-cancel">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => respond('accept')} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-accept-btn"><CheckCircle2 className="h-4 w-4 mr-1" /> I accept this statement</Button>
              <Button variant="outline" onClick={() => setMode('query')} className="border-amber-300 text-amber-700" data-testid="confirm-query-btn"><MessageSquareWarning className="h-4 w-4 mr-1" /> Raise a query</Button>
            </div>
          )
        )}
      </div>
    </div>
  );
}
